import type { MemberBetRecords } from "../bet-types";

// Yibo gamerecordv2 gameType: 1 live, 2 fishing, 3 slots, 4 lottery, 5 sports, 6 board, 7 esports.
export const betCategories = [
  { id: "slots", type: 3, name: "电子" }, { id: "live", type: 1, name: "真人" },
  { id: "board", type: 6, name: "棋牌" }, { id: "fishing", type: 2, name: "捕鱼" },
  { id: "lottery", type: 4, name: "彩票" }, { id: "sports", type: 5, name: "体育" },
  { id: "esports", type: 7, name: "电竞" },
] as const;

export type GameStatisticsRange = { start: number; end: number };

/** This is collection freshness, not a guarantee of complete upstream betting data. Range is in milliseconds. */
export function gameCollectionCoverage(data: MemberBetRecords | null, range: GameStatisticsRange, now = Date.now()) {
  if (!data?.lastSyncedAt) return { current: false, detail: "游戏记录尚未同步。" };
  const { from, through, delaySeconds } = data.coverage ?? {};
  if (typeof from !== "number" || typeof through !== "number" || !Number.isFinite(from) || !Number.isFinite(through) || from > through) {
    return { current: false, detail: "采集覆盖范围待确认，当前仅显示已收到的记录。" };
  }
  const start = Math.floor(range.start / 1000);
  const end = Math.floor((range.end - 1) / 1000);
  const currentTime = Math.floor(now / 1000);
  const delay = Number.isFinite(delaySeconds) && delaySeconds >= 0 ? delaySeconds : 300;
  if (Math.min(end, currentTime - delay) < start) return { current: false, detail: "当前时段尚未进入采集窗口，请稍后查看。" };
  if (through < start) return { current: false, detail: "数据追赶中，采集进度尚未到达当前查询日期。" };
  if (from > start) return { current: false, detail: "采集起点晚于查询起点，历史记录可能缺失，以下仅显示已收到的记录。" };
  // The live collector is five minutes delayed; its status considers over ten minutes behind stale.
  // Completed historical periods need their actual end, not the live-window tolerance.
  const requiredThrough = end < currentTime ? end : Math.max(start, currentTime - Math.max(600, delay));
  if (through < requiredThrough) return { current: false, detail: "数据追赶中，采集进度尚未覆盖当前查询时段，以下仅显示已收到的记录。" };
  return { current: true, detail: "按当前采集进度展示，近期注单仍可能延迟或修订。" };
}

/** There is no shared denomination in the source records. Never add different providers. */
export function todayGameSummary(data: MemberBetRecords | null, range: GameStatisticsRange, now = Date.now()) {
  if (!data?.lastSyncedAt) return { count: "—", profit: "—", detail: "同步场馆注单后显示今日数据。" };
  const coverage = gameCollectionCoverage(data, range, now);
  const describe = (detail: string) => coverage.current ? detail : `${coverage.detail} ${detail}`;
  const count = data.total.toFixed(2);
  if (!data.total) return coverage.current
    ? { count, profit: "0.00", detail: "截至已采集进度暂未收到今日记录，近期注单仍可能延迟。返水金额待正式规则启用。" }
    : { count: "—", profit: "—", detail: `${coverage.detail} 暂不能确认今日注单笔数或盈亏为零。` };
  const rows = data.dailyTotalsByProvider;
  if (!rows) return { count, profit: "—", detail: describe("盈亏汇总尚未提供，请进入游戏管理查看。") };
  const providers = new Set(rows.map(row => row.provider));
  if (providers.size > 1) return { count, profit: "分场馆", detail: describe("场馆币种尚未统一，盈亏在明细中分别显示。") };
  const total = data.totalsByProvider.find(row => providers.has(row.provider));
  if (!total?.settledCount) return { count, profit: "—", detail: describe("今日已收到的注单暂无已结算结果。") };
  return { count, profit: total.netAmount, detail: describe(`${total.provider} 已结算盈亏，金额为场馆原始单位。`) };
}
